import * as React from 'react';
import { Amplify, Auth } from 'aws-amplify';
import config from '../config';

// AWS Amplify Configuration
Amplify.configure({
  Auth: {
    userPoolId: config.cognito.userPoolId,
    userPoolWebClientId: config.cognito.clientId,
    region: config.cognito.region,
    authenticationFlowType: 'USER_PASSWORD_AUTH',
    mandatorySignIn: true,
    oauth: {
      domain: '',
      scope: ['email', 'openid', 'profile'],
      redirectSignIn: window.location.origin,
      redirectSignOut: window.location.origin,
      responseType: 'code'
    }
  },
});

export interface UserProfile {
  id?: string; // For backward compatibility
  sub: string;
  email: string;
  email_verified: boolean;
  given_name?: string;
  family_name?: string;
  name?: string;
  fullKoreanName: string;
  'cognito:username': string;
}

export interface AuthState {
  isAuthenticated: boolean;
  initialized: boolean;
  tokenReady: boolean; // Add token ready state
  token?: string;
  userProfile?: UserProfile;
  isAdmin: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  signUp: (username: string, password: string, name: string, email: string) => Promise<void>;
  confirmSignUp: (username: string, code: string) => Promise<void>;
  resendConfirmationCode: (username: string) => Promise<void>;
  forgotPassword: (username: string) => Promise<void>;
  forgotPasswordSubmit: (username: string, code: string, newPassword: string) => Promise<void>;
}

const AuthContext = React.createContext<AuthState | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [initialized, setInitialized] = React.useState(false);
  const [tokenReady, setTokenReady] = React.useState(false); // Add token ready state
  const [token, setToken] = React.useState<string>();
  const [userProfile, setUserProfile] = React.useState<UserProfile>();
  const [isAdmin, setIsAdmin] = React.useState(false);

  // Initialize auth state
  React.useEffect(() => {
    const initAuth = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const session = await Auth.currentSession();
        
        setIsAuthenticated(true);
        const accessToken = session.getAccessToken().getJwtToken();
        setToken(accessToken);
        // Only set tokenReady to true after token is actually set
        if (accessToken) {
          setTokenReady(true);
        }

        // Check admin group from access token payload
        const groups = session.getAccessToken().payload?.['cognito:groups'] || [];
        setIsAdmin(Array.isArray(groups) && groups.includes('admin'));
        
        const profile: UserProfile = {
          id: user.username,
          sub: user.username,
          email: user.attributes?.email || '',
          email_verified: user.attributes?.email_verified || false,
          given_name: user.attributes?.given_name || '',
          family_name: user.attributes?.family_name || '',
          name: user.attributes?.name || '',
          fullKoreanName: user.attributes?.name || '',
          'cognito:username': user.username,
        };
        
        setUserProfile(profile);
      } catch (error) {
        setIsAuthenticated(false);
        setToken(undefined);
        setTokenReady(false);
        setUserProfile(undefined);
      } finally {
        setInitialized(true);
      }
    };

    initAuth();
  }, []);

  // Monitor token changes and update tokenReady accordingly
  React.useEffect(() => {
    setTokenReady(!!token);
  }, [token]);

  const login = React.useCallback(async (email: string, password: string) => {
    try {
      const user = await Auth.signIn(email, password);
      const session = await Auth.currentSession();
      
      const accessToken = session.getAccessToken().getJwtToken();

      // Check admin group from access token payload
      const groups = session.getAccessToken().payload?.['cognito:groups'] || [];
      setIsAdmin(Array.isArray(groups) && groups.includes('admin'));

      // 모든 상태를 한 번에 설정하고 완료 후 페이지 전환
      await Promise.all([
        new Promise<void>(resolve => {
          setIsAuthenticated(true);
          resolve();
        }),
        new Promise<void>(resolve => {
          setToken(accessToken);
          resolve();
        }),
        new Promise<void>(resolve => {
          setTokenReady(!!accessToken);
          resolve();
        })
      ]);
      
      const profile: UserProfile = {
        id: user.username,
        sub: user.username,
        email: user.attributes?.email || email,
        email_verified: user.attributes?.email_verified || false,
        given_name: user.attributes?.given_name || '',
        family_name: user.attributes?.family_name || '',
        name: user.attributes?.name || '',
        fullKoreanName: user.attributes?.name || '',
        'cognito:username': user.username,
      };
      
      setUserProfile(profile);
      
      // 모든 상태가 설정된 후 페이지 전환 (약간의 지연 추가)
      setTimeout(() => {
        // React Router의 navigate를 사용하여 페이지 전환
        if (typeof window !== 'undefined' && window.location.pathname !== '/map') {
          window.location.href = '/map';
        }
      }, 200); // 200ms 지연
      
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }, []);

  const logout = React.useCallback(async () => {
    try {
      await Auth.signOut();
      setIsAuthenticated(false);
      setToken(undefined);
      setTokenReady(false);
      setUserProfile(undefined);
      setIsAdmin(false);
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  }, []);

  const signUpFunc = React.useCallback(async (username: string, password: string, name: string, email: string) => {
    try {
      const nameArray = name.split(' ');
      const familyName = nameArray[0] || '';
      const givenName = nameArray.slice(1).join(' ') || '';
      
      await Auth.signUp({
        username,
        password,
        attributes: {
          email,
          name,
          given_name: givenName,
          family_name: familyName,
        },
      });
    } catch (error) {
      console.error('Sign up failed:', error);
      throw error;
    }
  }, []);

  const confirmSignUpFunc = React.useCallback(async (email: string, code: string) => {
    try {
      await Auth.confirmSignUp(email, code);
    } catch (error) {
      console.error('Confirmation failed:', error);
      throw error;
    }
  }, []);

  const resendConfirmationCode = React.useCallback(async (email: string) => {
    try {
      await Auth.resendSignUp(email);
    } catch (error) {
      console.error('Resend confirmation failed:', error);
      throw error;
    }
  }, []);

  const forgotPasswordFunc = React.useCallback(async (username: string) => {
    try {
      await Auth.forgotPassword(username);
    } catch (error) {
      console.error('Forgot password failed:', error);
      throw error;
    }
  }, []);

  const forgotPasswordSubmitFunc = React.useCallback(async (username: string, code: string, newPassword: string) => {
    try {
      await Auth.forgotPasswordSubmit(username, code, newPassword);
    } catch (error) {
      console.error('Password reset failed:', error);
      throw error;
    }
  }, []);

  const authState: AuthState = {
    isAuthenticated,
    initialized,
    tokenReady,
    token,
    userProfile,
    isAdmin,
    login,
    logout,
    signUp: signUpFunc,
    confirmSignUp: confirmSignUpFunc,
    resendConfirmationCode,
    forgotPassword: forgotPasswordFunc,
    forgotPasswordSubmit: forgotPasswordSubmitFunc,
  };

  return <AuthContext.Provider value={authState}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthState => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <>{children}</> : null;
}